<template>
  <NavBar pageName="HistoryView"></NavBar>
  <v-container style="margin-top: 185px;">
    <!-- Title -->
    <div class="page-header d-flex justify-space-between align-center mb-4">
      <h1>{{ t.menu.history || 'History' }}</h1>
    </div>

    <!-- Search Filters -->
    <v-card class="pa-4 mb-4">
      <v-row align="center">
        <!-- IMSI -->
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.imsi_code"
            label="IMSI"
            clearable
            dense
            outlined
            hide-details
          ></v-text-field>
        </v-col>

        <!-- MSISDN -->
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.msisdn_code"
            label="MSISDN"
            clearable
            dense
            outlined
            hide-details
          ></v-text-field>
        </v-col>

        <!-- Client -->
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.client_id"
            :label="t.users?.client || 'Client'"
            :items="clientsForSelect"
            :loading="loadingClients"
            clearable
            dense
            outlined
            hide-details
          ></v-autocomplete>
        </v-col>

        <!-- User -->
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.user_id"
            :label="t.menu?.users || 'User'"
            :items="usersForSelect"
            :loading="loadingUsers"
            clearable
            dense
            outlined
            hide-details
          ></v-autocomplete>
        </v-col>

        <!-- Action Type -->
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.action_type"
            :label="t.common?.action || 'Action'"
            :items="actionTypes"
            clearable
            dense
            outlined
            hide-details
          ></v-autocomplete>
        </v-col>
      </v-row>

      <v-row align="center" class="mt-2">
        <!-- Date From -->
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.date_from"
            :label="t.common?.from_date || 'From Date'"
            type="date"
            clearable
            dense
            outlined
            hide-details
          ></v-text-field>
        </v-col>

        <!-- Date To -->
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.date_to"
            :label="t.common?.to_date || 'To Date'"
            type="date"
            clearable
            dense
            outlined
            hide-details
          ></v-text-field>
        </v-col>

        <!-- Limit -->
        <v-col cols="12" md="2">
          <v-select
            v-model="filters.limit"
            :label="t.common?.limit || 'Limit'"
            :items="[50, 100, 200, 500]"
            dense
            outlined
            hide-details
          ></v-select>
        </v-col>

        <!-- Search & Reset buttons -->
        <v-col cols="12" md="4">
          <v-btn color="primary" @click="loadHistory" :loading="loading" class="mr-2">
            <v-icon class="mr-1">mdi-magnify</v-icon>
            {{ t.common.search }}
          </v-btn>
          <v-btn variant="outlined" @click="resetFilters">
            {{ t.common.reset }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Results count -->
    <div class="mb-2 text-caption" v-if="historyLoaded">
      {{ t.common?.showing || 'Showing' }} {{ histories.length }} {{ t.common?.records || 'records' }}
    </div>

    <!-- History Table -->
    <v-card>
      <v-table density="compact" hover>
        <thead>
          <tr class="bg-grey-lighten-3">
            <th>{{ t.common?.date || 'Date' }}</th>
            <th>{{ t.common?.action || 'Action' }}</th>
            <th>{{ t.menu?.users || 'User' }}</th>
            <th>{{ t.users?.client || 'Client' }}</th>
            <th>IMSI</th>
            <th>MSISDN</th>
            <th>{{ t.common?.details || 'Details' }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in histories" :key="h.id" class="history-row">
            <td class="text-no-wrap">{{ formatDate(h.created_at) }}</td>
            <td>
              <v-chip size="x-small" :color="getActionColor(h.action)">
                {{ h.action }}
              </v-chip>
            </td>
            <td>{{ h.user_name || h.user_username || '-' }}</td>
            <td>{{ h.client_name || '-' }}</td>
            <td>{{ h.imsi_code || '-' }}</td>
            <td>{{ h.msisdn_code || '-' }}</td>
            <td>
              <v-btn v-if="h.other_params" icon variant="text" size="small" @click="showDetails(h)">
                <v-icon size="20">mdi-information-outline</v-icon>
              </v-btn>
              <span v-else>-</span>
            </td>
          </tr>
          <tr v-if="histories.length === 0 && historyLoaded">
            <td colspan="7" class="text-center pa-4 text-grey">
              {{ t.common?.no_results || 'No results found' }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="500px">
      <v-card v-if="selectedHistory">
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-information</v-icon>
          {{ selectedHistory.action }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-table density="compact">
            <tbody>
              <tr>
                <td class="font-weight-medium" width="40%">{{ t.common?.date || 'Date' }}</td>
                <td>{{ formatDate(selectedHistory.created_at) }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.menu?.users || 'User' }}</td>
                <td>{{ selectedHistory.user_name || selectedHistory.user_username || '-' }}</td>
              </tr>
              <tr v-if="selectedHistory.client_name">
                <td class="font-weight-medium">{{ t.users?.client || 'Client' }}</td>
                <td>{{ selectedHistory.client_name }}</td>
              </tr>
              <tr v-if="selectedHistory.imsi_code">
                <td class="font-weight-medium">IMSI</td>
                <td>{{ selectedHistory.imsi_code }}</td>
              </tr>
              <tr v-if="selectedHistory.msisdn_code">
                <td class="font-weight-medium">MSISDN</td>
                <td>{{ selectedHistory.msisdn_code }}</td>
              </tr>
              <tr v-if="selectedHistory.service_name">
                <td class="font-weight-medium">{{ t.menu?.services || 'Service' }}</td>
                <td>{{ selectedHistory.service_name }}</td>
              </tr>
            </tbody>
          </v-table>

          <v-divider class="my-3" v-if="selectedHistory.other_params"></v-divider>

          <div v-if="selectedHistory.other_params">
            <div class="font-weight-bold mb-2">{{ t.common?.details || 'Details' }}:</div>
            <v-table density="compact">
              <tbody>
                <tr v-for="(value, key) in selectedHistory.other_params" :key="key">
                  <td class="font-weight-medium" width="40%">{{ formatKey(key) }}</td>
                  <td>{{ formatValue(value) }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="detailsDialog = false">{{ t.common.close }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import { BASE_URL } from "@/composables/url_config";
import { t as tRef } from "@/composables/useLanguages";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { getClientTree } from "@/composables/useGetClientsAPI";
import axios from 'axios';

export default defineComponent({
  name: 'HistoryView',
  components: { NavBar },
  data() {
    return {
      token: '',
      user: null,
      loading: false,
      historyLoaded: false,
      histories: [],
      actionTypes: [],

      // Filters
      filters: {
        imsi_code: '',
        msisdn_code: '',
        client_id: null,
        user_id: null,
        action_type: null,
        date_from: '',
        date_to: '',
        limit: 100
      },

      // Clients & Users for dropdowns
      clientsList: [],
      usersList: [],
      loadingClients: false,
      loadingUsers: false,

      // Details dialog
      detailsDialog: false,
      selectedHistory: null
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    clientsForSelect() {
      return this.clientsList.map(c => ({
        value: c.id,
        title: c.displayName || c.name
      }));
    },
    usersForSelect() {
      return this.usersList.map(u => ({
        value: u.id,
        title: u.name || u.username
      }));
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    formatValue(value) {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
    getActionColor(action) {
      if (!action) return 'grey';
      if (action.includes('delete') || action.includes('terminate')) return 'error';
      if (action.includes('suspend')) return 'warning';
      if (action.includes('create') || action.includes('add') || action.includes('activate')) return 'success';
      if (action.includes('update') || action.includes('edit') || action.includes('toggle')) return 'info';
      return 'grey';
    },
    showDetails(history) {
      this.selectedHistory = history;
      this.detailsDialog = true;
    },

    async loadHistory() {
      this.loading = true;
      try {
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json"
        };

        const params = {};
        if (this.filters.imsi_code) params.imsi_code = this.filters.imsi_code;
        if (this.filters.msisdn_code) params.msisdn_code = this.filters.msisdn_code;
        if (this.filters.client_id) params.client_id = this.filters.client_id;
        if (this.filters.user_id) params.user_id = this.filters.user_id;
        if (this.filters.action_type) params.action_type = this.filters.action_type;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        if (this.filters.limit) params.limit = this.filters.limit;

        const response = await axios.get(`${BASE_URL}/home/getHistoryApi`, { headers, params });

        if (response.data.error) {
          alert(response.data.message || 'Error loading history');
        } else {
          this.histories = response.data.histories || [];
          this.actionTypes = response.data.action_types || [];
        }
        this.historyLoaded = true;
      } catch (error) {
        console.error("Error loading history:", error);
        alert("Error loading history");
      }
      this.loading = false;
    },

    resetFilters() {
      this.filters = {
        imsi_code: '',
        msisdn_code: '',
        client_id: null,
        user_id: null,
        action_type: null,
        date_from: '',
        date_to: '',
        limit: 100
      };
    },

    // Build flat list from client tree with indentation
    buildClientsList(clientsTree) {
      const list = [];
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return;
        clients.forEach(client => {
          const indent = level > 0 ? '—'.repeat(level) + ' ' : '';
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level
          });
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1);
          }
        });
      };
      addClientsRecursively(clientsTree);
      return list;
    },

    async fetchClients() {
      try {
        this.loadingClients = true;
        const response = await getClientTree(this.token, this.user.client_id);
        if (!response.error && response.clients_list) {
          this.clientsList = this.buildClientsList(response.clients_list);
        }
      } catch (error) {
        console.error("Error loading clients:", error);
      } finally {
        this.loadingClients = false;
      }
    },

    async fetchUsers() {
      try {
        this.loadingUsers = true;
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json"
        };
        const response = await axios.get(`${BASE_URL}/home/getClientUsers`, {
          headers,
          params: { client_id: this.user.client_id, include_subclients: true }
        });
        if (!response.data.error) {
          this.usersList = response.data.users || [];
        }
      } catch (error) {
        console.error("Error loading users:", error);
      } finally {
        this.loadingUsers = false;
      }
    }
  },
  created() {
    const { getLoggedIn, user, token } = useGetLoggedIn();

    getLoggedIn().then(() => {
      this.token = token.value;
      this.user = user.value;

      // Load dropdowns data
      this.fetchClients();
      this.fetchUsers();

      // Load history on page load
      this.loadHistory();
    });
  }
});
</script>

<style scoped>
.page-header {
  margin-bottom: 20px;
}
.history-row:hover {
  background-color: #f5f5f5;
}
</style>
